import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const propTypes = {
  to: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.node,
    PropTypes.element,
  ]).isRequired,
  onClick: PropTypes.func,
  handleClick: PropTypes.func,
  disabled: PropTypes.bool,
  appearance: PropTypes.oneOf(['black', 'primary', 'bare', 'light', 'default', 'subtle']),
  className: PropTypes.string,
  backgroundColor: PropTypes.string,
  color: PropTypes.string,
};

const defaultProps = {
  to: '',
  handleClick: null,
  disabled: false,
  appearance: 'default',
  backgroundColor: null,
  color: null,
};

function getButtonClasses({ appearance, size, className, backgroundColor, disabled }) {
  return clsx(
    // Base styles
    'flex items-center flex-shrink text-base rounded no-underline transition-colors duration-200 font-medium',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300',
    'py-2 px-2',
    {
      // Black appearance
      'bg-black hover:bg-gray-800 text-white': appearance === 'black' && !backgroundColor,

      // Primary appearance
      'bg-white hover:bg-gray-50 text-gray-900 border border-gray-200':
        appearance === 'primary' && !backgroundColor,

      // Bare appearance
      'bg-transparent hover:bg-transparent text-gray-900 hover:text-gray-700 border-transparent':
        appearance === 'bare',

      // Light appearance
      'bg-white text-gray-900 border border-gray-200 hover:border-gray-400': appearance === 'light',

      // Subtle appearance
      'bg-gray-100 hover:bg-gray-200 text-gray-900': appearance === 'subtle',

      // Default appearance (if none of the above)
      'bg-white text-gray-900': appearance === 'default',

      // Size variations
      'w-full justify-center': size === 'full',
      'px-2 py-1 text-sm': size === 'small',

      // Disabled state
      '!cursor-not-allowed': disabled,

      // Focus state when no custom background
      'focus-visible:border-gray-300': !backgroundColor,
    },
    className,
  );
}

const Button = ({ className, size = 'default', backgroundColor, color, ...props }) => {
  const { to, appearance, handleClick, disabled, ...domProps } = props;
  const onClick = props.onClick || handleClick || null;

  const Element = to ? 'a' : 'button';
  const classes = getButtonClasses({ appearance, size, className, backgroundColor, disabled });

  const style = backgroundColor ? { backgroundColor, color } : undefined;

  return (
    <Element
      className={classes}
      style={style}
      onClick={onClick}
      href={to || null}
      disabled={disabled}
      {...domProps}
    >
      {props.children}
    </Element>
  );
};

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
